import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type Konva from 'konva'
import {
  X, Play, Pause, SkipBack, SkipForward, RotateCcw, AlertTriangle,
  ChevronDown, ZoomIn, ZoomOut, Maximize2, LocateFixed, OctagonX,
} from 'lucide-react'
import {
  getYardMap,
  getTrackCars,
  getAvailableLocomotives,
  buildPreviewTimeline,
  MAX_PLAUSIBLE_CAR_LENGTH_FT,
  UNUSUAL_CAR_LENGTH_FT,
  snapshotAt,
  usePreviewPlayback,
  PREVIEW_SPEEDS,
  DEFAULT_SPEED_FPS,
  type SwitchList,
  type PreviewGeo,
  type PreviewSnapshot,
  type TrackFeatureLike,
  type SwitchFeatureLike,
  type CarMeta,
  type CarStart,
  type InterchangeRailCar,
} from '@nyos/shared'
import YardMapCanvas from './YardMapCanvas'
import { collapseStaleNotices, describeWarning } from './previewNotices'
import { calculateBoundingBox, initializeCameraState, type CanvasOffset, type MapViewState } from '../utils/yardMapUtils'
import {
  gestureStart,
  gestureMove,
  gestureEnd,
  shouldPauseFollow,
  isPinchRatioSignificant,
  panCamera,
  zoomAtPoint,
  tapSuppressedUntil,
  IDLE_GESTURE,
  type GestureState,
} from '../utils/previewGestures'
import { useStageSize } from '../hooks/useStageSize'
import type { GisLayoutOrientation } from '../types/interchange'

/**
 * Animated switch-list preview modal (Plan 26 Phase 1d). Hosts the read-only
 * yard map + playback transport; the consist itself comes from the pure
 * shared engine (shared/src/animation) — this component only fetches
 * geometry/inventory and projects.
 *
 * Stale-map guard: missing tracks make the timeline fatal (playback
 * disabled); missing switches degrade to endpoint fallback with a notice.
 */

interface SwitchListPreviewMapModalProps {
  switchList: SwitchList
  facilityId: string
  onClose: () => void
  /** Rail-car id of the locomotive powering the list — lets the sim seed
   *  the loco at its real track position instead of treating its
   *  inventory row as a parked body. */
  locomotiveId?: string
  /** Manifest cars from the interchange payload (dispatch path). Unseated
   *  arrivals have no inventory row and aren't in `item.cars`, so this is
   *  the only mark source that turns their uuids into reporting marks in
   *  sim notices. */
  railCars?: InterchangeRailCar[]
  /** Caller-computed advisory lines (e.g. locomotive mismatch between the
   *  picker and the cut's assigned loco) — rendered in the amber advisory
   *  strip, never blocking. */
  extraNotices?: string[]
}

/** Internal `staleReasons` strings → operator-readable explanation. Unknown
 *  strings fall through verbatim — new notice types need a case here. */
const describeNotice = (raw: string): string => {
  let m: RegExpMatchArray | null
  if ((m = raw.match(/^data-quality: '([^']+)' and '([^']+)' overlap on '([^']+)'/))) {
    return `Two cars overlap in the stored positions on ${m[3]}: ${m[1]} and ${m[2]}. Stored data issue — the preview keeps them in place.`
  }
  if ((m = raw.match(/missing toTrack '([^']+)'/))) {
    return `Track "${m[1]}" is not in the yard map — that leg cannot be drawn.`
  }
  if ((m = raw.match(/missing switch '([^']+)'/))) {
    return `Switch "${m[1]}" is not in the yard map — its throws may not line up visually.`
  }
  if (raw.includes('no junction anchor')) {
    return 'A leg has no switch junction to anchor to — it cannot be drawn.'
  }
  if ((m = raw.match(/'([^']+)' too short — (\d+) ft consist vs (\d+) ft clear span/))) {
    return `${m[1]} is too short: ${m[2]} ft of cars for ${m[3]} ft of clear track — the cut may not clear the switch.`
  }
  if ((m = raw.match(/coupler gap short of parked cars on '([^']+)'/))) {
    return `A move was shortened to avoid running into parked cars on ${m[1]} — the planned stop may be off.`
  }
  if ((m = raw.match(/junction gap (\S+)/))) {
    return `A track end is not attached to its switch (${m[1]} of drift) — the consist may hop across the gap.`
  }
  if ((m = raw.match(/^blocked-pass: '([^']+)' skipped — (.+)$/))) {
    return `Pass skipped — backend marked it BLOCKED: ${m[2]}`
  }
  if ((m = raw.match(/hard-stops short of parked (?:car|body) '([^']+)' on '([^']+)'/))) {
    return `The consist stops a coupler-gap short of parked car ${m[1]} on ${m[2]} — the planned leg would have driven through it.`
  }
  if (raw.includes('no animatable steps')) {
    return 'Nothing to animate — the switch list is empty or every pass is blocked.'
  }
  return raw
}

/** Timeline feet → virtual seconds at the 1× pace. */
const fmtTime = (feet: number): string => {
  const s = Math.floor(feet / DEFAULT_SPEED_FPS)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

const canvasOffset: CanvasOffset = { x: 0, y: 0 }

const SwitchListPreviewMapModal: React.FC<SwitchListPreviewMapModalProps> = ({
  switchList,
  facilityId,
  onClose,
  locomotiveId,
  railCars,
  extraNotices,
}) => {
  const stageSize = useStageSize({ widthFactor: 1, heightOffset: 140 })
  const [noticesOpen, setNoticesOpen] = useState(false)
  const geoQuery = useQuery({
    queryKey: ['yard-map', facilityId],
    queryFn: () => getYardMap(facilityId),
    enabled: !!facilityId,
    staleTime: 60_000,
  })

  const geo: PreviewGeo | null = useMemo(() => {
    const features = geoQuery.data?.features
    if (!features) return null
    const tracks = new Map<string, TrackFeatureLike>(
      features
        .filter((f: any) => f.properties?.type === 'track')
        .map((f: any) => [f.id as string, f as TrackFeatureLike])
    )
    const switches = new Map<string, SwitchFeatureLike>(
      features
        .filter((f: any) => f.properties?.type === 'switch')
        .map((f: any) => [f.id as string, f as SwitchFeatureLike])
    )
    return { tracks, switches }
  }, [geoQuery.data])

  // Car start positions + lengths for every track referenced by the list.
  // Tue fix: the sim seats the loco only when its rail_car row is in
  // carStarts — which requires fetching its home track. The list's
  // micro-steps may never reference that track (UC1: loco on Main Cont
  // while the plan touches Main + Siding), so resolve the loco record
  // and add its current track to the fetch set.
  const locoQuery = useQuery({
    queryKey: ['preview-locomotives', facilityId],
    queryFn: () => getAvailableLocomotives(facilityId),
    enabled: !!facilityId && !!locomotiveId,
  })
  const locoTrackId =
    locoQuery.data?.find(l => l.id === locomotiveId)?.kinematics?.currentTrackId

  const trackIds = useMemo(() => {
    const ids = new Set<string>()
    for (const item of switchList.items ?? []) {
      for (const step of item.microSteps ?? []) {
        if (step.fromTrackId) ids.add(step.fromTrackId)
        if (step.toTrackId) ids.add(step.toTrackId)
      }
    }
    if (locoTrackId) ids.add(locoTrackId)
    return [...ids]
  }, [switchList, locoTrackId])

  const inventoryQuery = useQuery({
    queryKey: ['preview-inventory', facilityId, trackIds],
    enabled: !!facilityId && trackIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const inventories = await Promise.all(
        trackIds.map(t => getTrackCars(facilityId, t).catch(() => null))
      )
      const starts = new Map<string, CarStart>()
      const meta = new Map<string, CarMeta>()
      const notes: string[] = []
      for (const inv of inventories) {
        if (!inv) continue
        for (const car of inv.cars ?? []) {
          if (car.linearOffsetFeet == null) {
            // Never silently seat at offset 0 — exclude the row and say
            // so. (Loco rows skipped this way still trigger the sim's
            // "not found in track inventory" notice.)
            notes.push(
              `'${car.reportingMark ?? car.railCarId}' on '${inv.trackId}' has ` +
              `no stored track offset — excluded from preview inventory`
            )
            continue
          }
          starts.set(car.railCarId, {
            trackId: inv.trackId,
            offsetFeet: car.linearOffsetFeet,
            assetType: car.assetType ?? undefined,
          })
          meta.set(car.railCarId, {
            lengthFeet: car.lengthFeet ?? 50,
            reportingMark: car.reportingMark,
            hazmatClass: car.hazmatClass,
            hazardous: car.hazardous,
          })
        }
      }
      return { starts, meta, notes }
    },
  })

  const geoWithInventory: PreviewGeo | null = useMemo(
    () =>
      geo && inventoryQuery.data
        ? { ...geo, carStarts: inventoryQuery.data.starts, locomotiveId }
        : geo,
    [geo, inventoryQuery.data, locomotiveId]
  )
  // Manifest lengths beyond plausibility are dropped in the carMeta merge
  // below — collected here so they surface as a data-quality notice.
  const implausibleManifestLengths = useMemo(
    () =>
      (railCars ?? []).filter(
        c => c.lengthFeet != null && c.lengthFeet > MAX_PLAUSIBLE_CAR_LENGTH_FT
      ),
    [railCars]
  )
  // >85 ft but ≤120 ft — plausible enough to keep, unusual enough to flag.
  const unusualManifestLengths = useMemo(
    () =>
      (railCars ?? []).filter(
        c =>
          c.lengthFeet != null &&
          c.lengthFeet > UNUSUAL_CAR_LENGTH_FT &&
          c.lengthFeet <= MAX_PLAUSIBLE_CAR_LENGTH_FT
      ),
    [railCars]
  )

  // Inventory meta + hydrated item.cars detail (authoritative marks/hazmat
  // for cast cars; inventory covers parked non-cast cars too).
  const carMeta = useMemo(() => {
    // Lowest-precedence first: manifest marks cover unseated cars that
    // have no inventory row; inventory + item.cars overwrite with richer
    // detail where present.
    const meta = new Map<string, CarMeta>()
    for (const c of railCars ?? []) {
      // Implausible manifest lengths (bad EDI data — e.g. 420 ft for a
      // boxcar) are ignored, never trusted for render/packing; the
      // discarded values are surfaced via implausibleManifestLengths.
      const plausible =
        c.lengthFeet != null && c.lengthFeet <= MAX_PLAUSIBLE_CAR_LENGTH_FT
      meta.set(c.id, {
        lengthFeet: plausible ? c.lengthFeet : 50,
        reportingMark: c.reportingMark,
        hazmatClass: c.hazmatClass,
      })
    }
    for (const [id, m] of inventoryQuery.data?.meta ?? []) meta.set(id, m)
    for (const item of switchList.items ?? []) {
      for (const c of item.cars ?? []) {
        const existing = meta.get(c.railCarId)
        meta.set(c.railCarId, {
          lengthFeet: existing?.lengthFeet ?? c.lengthFeet ?? 50,
          reportingMark: c.reportingMark ?? existing?.reportingMark,
          hazmatClass: c.hazmatClass ?? existing?.hazmatClass,
          hazardous: c.hazardous ?? existing?.hazardous,
        })
      }
    }
    return meta
  }, [inventoryQuery.data, switchList, railCars])

  const previewCarLengths = useMemo(
    () => new Map([...carMeta].map(([id, m]) => [id, m.lengthFeet])),
    [carMeta]
  )

  // F-UI-2: backend reason strings name blocker/missing cars by UUID —
  // rewrite any resolvable id as its reporting mark; unregistered ids
  // degrade to the 8-char prefix instead of the full uuid.
  /** Track UUID → display name, for ids embedded in backend/sim reason
   *  strings the emit site couldn't resolve (carMeta has cars only). */
  const trackNames = useMemo(() => {
    const m = new Map<string, string>()
    for (const f of geoQuery.data?.features ?? []) {
      if (f.properties?.type === 'track' && f.id != null && f.properties?.name) {
        m.set(String(f.id).toLowerCase(), String(f.properties.name))
      }
    }
    return m
  }, [geoQuery.data])

  const markOf = (text: string | null | undefined): string =>
    text?.replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      id =>
        carMeta.get(id.toLowerCase())?.reportingMark
        ?? trackNames.get(id.toLowerCase())
        ?? `${id.slice(0, 8)}…`
    ) ?? ''

  const build = useMemo(
    () =>
      geoWithInventory
        ? buildPreviewTimeline(switchList, geoWithInventory, carMeta)
        : null,
    [switchList, geoWithInventory, carMeta]
  )

  const timeline = build?.timeline ?? null

  // Marker legend: when EVERY cast car is approximated (inbound EDI — the
  // whole cut parks at the couple junction), one note explains the dashed
  // bodies and per-car dots are suppressed. Mixed lists keep per-car dots.
  const allApprox = !!timeline && timeline.castCarIds.size > 0 &&
    [...timeline.castCarIds].every(id => timeline.approxCarIds.has(id))
  const anyApprox = !!timeline && timeline.approxCarIds.size > 0
  const hasHazmat = [...carMeta.values()].some(m => m.hazardous || !!m.hazmatClass)

  const playback = usePreviewPlayback(timeline)

  const snapshot: PreviewSnapshot | null = useMemo(
    () => (timeline && geoWithInventory ? snapshotAt(timeline, geoWithInventory, playback.feet) : null),
    [timeline, geoWithInventory, playback.feet]
  )

  // ---- Interactive camera -------------------------------------------------
  // Wheel/pan, +/- buttons, Fit-to-yard, and a smooth Follow that eases the
  // loco into frame at ~1.75 px/ft instead of recentering every frame.
  // Any manual wheel/drag pauses follow until the user hits Re-center.

  const FOLLOW_PX_PER_FT = 1.75
  const FOLLOW_DEADZONE_PX = 60

  const fitCamera = useMemo(() => {
    const features = geoQuery.data?.features
    if (!features) return null
    const bbox = calculateBoundingBox(
      features.filter((f: any) => f.properties?.type === 'track' || f.properties?.type === 'switch')
    )
    return initializeCameraState(bbox, stageSize.width, stageSize.height)
  }, [geoQuery.data])

  const [camera, setCamera] = useState<MapViewState | null>(null)
  const [followPaused, setFollowPaused] = useState(false)
  const dragRef = useRef<{ x: number; y: number; lon: number; lat: number } | null>(null)
  // Touch camera gestures (Plan 30 T8): one-finger pan / two-finger pinch
  // over the same camera as mouse drag. Refs, not state — coordinates
  // mutate per frame and must not re-render. Decision logic lives in
  // utils/previewGestures (pure — vitest-covered).
  const touchGesture = useRef<GestureState>(IDLE_GESTURE)
  // Swallows the synthesized tap a pan/pinch release would fire on any
  // future canvas tap handler — YardMap's handleTrackTap contract.
  const suppressTapUntil = useRef(0)

  // Wide-to-close reveal + fit on first geometry load.
  useEffect(() => {
    if (fitCamera && camera == null) setCamera(fitCamera)
  }, [fitCamera, camera])

  const cameraState = camera ?? fitCamera
  const feetPerDegLon = useMemo(() => {
    const lat = cameraState?.centerLat ?? 0
    return 364320 * Math.cos((lat * Math.PI) / 180)
  }, [cameraState?.centerLat])

  const minZoom = fitCamera ? fitCamera.zoomScale * 0.35 : 1
  const maxZoom = feetPerDegLon * 6 // ~6 px/ft cap

  /** Cursor-anchored zoom: the lon/lat under (sx,sy) stays put. */
  const zoomAt = (sx: number, sy: number, factor: number, source: MapViewState): MapViewState =>
    zoomAtPoint(sx, sy, factor, source, stageSize.width, stageSize.height, minZoom, maxZoom)

  // Smooth follow: ease the camera center/zoom toward the loco each render
  // of a new snapshot. Dead zone suppresses per-pixel jitter; manual input
  // pauses follow until Re-center is pressed.
  const locoLon = snapshot?.loco?.lon
  const locoLat = snapshot?.loco?.lat
  useEffect(() => {
    if (followPaused || !camera || locoLon == null || locoLat == null) return
    const targetZoom = Math.min(Math.max(feetPerDegLon * FOLLOW_PX_PER_FT, minZoom), maxZoom)
    const dxPx = (locoLon - camera.centerLon) * camera.zoomScale
    const dyPx = (camera.centerLat - locoLat) * camera.zoomScale
    const offCenter = Math.hypot(dxPx, dyPx)
    const zoomDrift = Math.abs(camera.zoomScale - targetZoom) / targetZoom
    if (offCenter < FOLLOW_DEADZONE_PX && zoomDrift < 0.08) return
    setCamera(c => {
      if (!c) return c
      const ease = 0.18
      const z = c.zoomScale + (targetZoom - c.zoomScale) * ease
      // Keep the eased center at loco-centered only when beyond dead zone.
      const cx = offCenter < FOLLOW_DEADZONE_PX ? c.centerLon : c.centerLon + (locoLon - c.centerLon) * ease
      const cy = offCenter < FOLLOW_DEADZONE_PX ? c.centerLat : c.centerLat + (locoLat - c.centerLat) * ease
      return { centerLon: cx, centerLat: cy, zoomScale: z }
    })
  }, [locoLon, locoLat, followPaused, camera, feetPerDegLon, minZoom, maxZoom])

  const onWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault()
    if (!camera) return
    setFollowPaused(true)
    const stage = e.target.getStage()
    const p = stage?.getPointerPosition() ?? { x: stageSize.width / 2, y: stageSize.height / 2 }
    setCamera(c => (c ? zoomAt(p.x, p.y, e.evt.deltaY < 0 ? 1.2 : 1 / 1.2, c) : c))
  }

  const onMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    // Touch gestures synthesize mouse events — while a touch gesture is
    // live the touch handlers own the camera; real mousedowns (mouse
    // users never set gesture mode) pass through unchanged.
    if (!camera || touchGesture.current.mode !== 'none') return
    const stage = e.target.getStage()
    const p = stage?.getPointerPosition()
    if (p) dragRef.current = { x: p.x, y: p.y, lon: camera.centerLon, lat: camera.centerLat }
  }
  const onMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (touchGesture.current.mode !== 'none') return
    const d = dragRef.current
    if (!d || !camera) return
    const stage = e.target.getStage()
    const p = stage?.getPointerPosition()
    if (!p) return
    setFollowPaused(true)
    const flipX = yardOrientation === 'REVERSED' ? -1 : 1
    setCamera(c => c && ({
      centerLon: d.lon - flipX * (p.x - d.x) / c.zoomScale,
      centerLat: d.lat + (p.y - d.y) / c.zoomScale,
      zoomScale: c.zoomScale,
    }))
  }
  const endDrag = () => { dragRef.current = null }

  // ---- Touch camera gestures (T8) --------------------------------------
  // One-finger drag pans via the same camera math as the mouse; two
  // fingers pinch-zoom around their midpoint (midpoint drift also pans).
  // Everything past the touch-event boundary is decided by the pure
  // previewGestures module; the canvas container already carries
  // touch-action:none so a drag on the map never scrolls the modal.

  const touchStageRect = (e: Konva.KonvaEventObject<TouchEvent>) =>
    e.target.getStage()?.container().getBoundingClientRect() ?? null

  const handleTouchStart = (e: Konva.KonvaEventObject<TouchEvent>) => {
    if (!camera) return
    const rect = touchStageRect(e)
    if (!rect) return
    const next = gestureStart(Array.from(e.evt.touches), rect)
    if (next.mode === 'pinch') e.evt.preventDefault()
    touchGesture.current = next
  }

  const handleTouchMove = (e: Konva.KonvaEventObject<TouchEvent>) => {
    if (!camera) return
    const rect = touchStageRect(e)
    if (!rect) return
    // Two fingers down: block native pinch/scroll even on the pan→pinch
    // anchor frame, where gestureMove returns no camera effect yet.
    if (e.evt.touches.length >= 2) e.evt.preventDefault()
    const result = gestureMove(touchGesture.current, Array.from(e.evt.touches), rect)
    touchGesture.current = result.gesture
    const move = result.move
    if (!move) return
    if (move.kind === 'pan') e.evt.preventDefault()
    // Manual touch input pauses Follow exactly like a mouse drag —
    // pinch always, pan only once past the tap dead zone.
    if (shouldPauseFollow(result.gesture, move)) setFollowPaused(true)
    const flipX = yardOrientation === 'REVERSED' ? -1 : 1
    if (move.kind === 'pan') {
      setCamera(c => c && panCamera(c, move.dx, move.dy, flipX))
    } else {
      setCamera(c => {
        if (!c) return c
        let next = panCamera(c, move.dx, move.dy, flipX)
        if (isPinchRatioSignificant(move.ratio)) {
          next = zoomAtPoint(move.midX, move.midY, move.ratio, next, stageSize.width, stageSize.height, minZoom, maxZoom)
        }
        return next
      })
    }
  }

  const handleTouchEnd = (e: Konva.KonvaEventObject<TouchEvent>) => {
    const rect = touchStageRect(e) ?? { left: 0, top: 0 }
    const { gesture, suppressTap } = gestureEnd(touchGesture.current, Array.from(e.evt.touches), rect)
    touchGesture.current = gesture
    if (suppressTap) suppressTapUntil.current = tapSuppressedUntil(Date.now())
  }

  const zoomButton = (factor: number) => {
    if (!camera) return
    setFollowPaused(true)
    setCamera(c => (c ? zoomAt(stageSize.width / 2, stageSize.height / 2, factor, c) : c))
  }
  const fitToYard = () => {
    if (!fitCamera) return
    setFollowPaused(true)
    setCamera(fitCamera)
  }
  const reCenter = () => {
    setFollowPaused(false)
    // Snap once so the loco re-enters frame immediately, then easing resumes.
    if (locoLon != null && locoLat != null) {
      setCamera(c => c && { ...c, centerLon: locoLon, centerLat: locoLat })
    }
  }

  const yardOrientation: GisLayoutOrientation =
    (geoQuery.data?.metadata?.gisLayoutOrientation as GisLayoutOrientation) ?? 'NORMAL'

  const loading = geoQuery.isPending || inventoryQuery.isPending
  const fatal = !!build && !build.ok
  const stale = build?.staleReasons ?? []
  const noticeGroups = useMemo(
    () =>
      collapseStaleNotices([
        ...stale,
        ...(inventoryQuery.data?.notes ?? []),
        ...(implausibleManifestLengths.length > 0
          ? [
              `data-quality: ${implausibleManifestLengths
                .map(c => `'${c.reportingMark ?? c.id}' (${c.lengthFeet} ft)`)
                .join(', ')} — manifest length exceeds ` +
                `${MAX_PLAUSIBLE_CAR_LENGTH_FT} ft, ignored — default car length used`,
            ]
          : []),
        ...(unusualManifestLengths.length > 0
          ? [
              `data-quality: ${unusualManifestLengths
                .map(c => `'${c.reportingMark ?? c.id}' (${c.lengthFeet} ft)`)
                .join(', ')} — manifest length over ` +
                `${UNUSUAL_CAR_LENGTH_FT} ft is unusual for yard switching, kept as-is`,
            ]
          : []),
      ]),
    [stale, inventoryQuery.data, implausibleManifestLengths, unusualManifestLengths]
  )
  // WARN-severity plan findings (item.warnings — UNSEATED_ARRIVAL names
  // the cars). Reworded for operators and always advisory/amber — they
  // never join the fatal notice panel or the blocked-pass banner.
  const warningGroups = useMemo(
    () =>
      collapseStaleNotices(
        [
          ...(extraNotices ?? []),
          ...(switchList.items ?? [])
            .flatMap(i => i.warnings ?? [])
            .map(describeWarning),
        ],
        { advisoryOnly: true }
      ),
    [switchList, extraNotices]
  )
  const blockedPasses = build?.blockedPasses ?? []
  const inconsistency = build?.inconsistency
  /** Where playback stops, if it stops early — the first blocked pass or
   *  the pass that hit an enforced inconsistency. */
  const stopAt = inconsistency ?? blockedPasses[0] ?? null
  // GeoJSON with the playback switch states applied — THROW_SWITCH
  // persists across passes until a later throw changes it back.
  const previewGeoData = useMemo(() => {
    const data = geoQuery.data
    const states = snapshot?.switchStates
    if (!data || !states || Object.keys(states).length === 0) return data
    return {
      ...data,
      features: (data.features ?? []).map((f: any) =>
        f.properties?.type === 'switch' && states[f.id]
          ? { ...f, properties: { ...f.properties, state: states[f.id] } }
          : f
      ),
    }
  }, [geoQuery.data, snapshot?.switchStates])

  const stepLabel = (() => {
    const s = snapshot?.activeStep
    if (!s) return '—'
    if (s.type === 'REVERSE') {
      return `Consist clears ${s.switchName ?? 'the switch'}, then shoves back through the switch`
    }
    const base = `${s.type}${s.switchName ? ` — ${s.switchName}` : ''}${s.fromTrackName || s.toTrackName ? ` · ${s.fromTrackName ?? ''}→${s.toTrackName ?? ''}` : ''}`
    // TEMP-UC1-APPROACH — the marker's span includes a display-only
    // same-track approach; the caption must say so, not just the strip.
    return snapshot?.activeSyntheticApproachFeet != null
      ? `${base} · Approach cut - illustrative, not a switch-list step`
      : base
  })()

  return (
    <div
      className="fixed inset-0 z-[110] flex flex-col bg-slate-950/95"
      onClick={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-700 bg-slate-900 shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-white">Animated Switch List Preview</span>
          {inconsistency && (
            <span
              className="px-2 py-0.5 rounded text-xs font-mono bg-red-900/60 text-red-300 border border-red-700"
              title={markOf(inconsistency.reason)}
            >
              PREVIEW INCONSISTENT
            </span>
          )}
          {blockedPasses.length > 0 && (
            <span
              className="px-2 py-0.5 rounded text-xs font-mono bg-red-900/60 text-red-300 border border-red-700"
              title="Passes not animated — the banner below names them and where playback stopped"
            >
              {blockedPasses.length} pass{blockedPasses.length > 1 ? 'es' : ''} not animated
            </span>
          )}
          {fatal && (
            <span className="px-2 py-0.5 rounded text-xs font-mono bg-red-900/60 text-red-300 border border-red-700">
              PLAYBACK DISABLED — STALE GEOMETRY
            </span>
          )}
          {fatal && stale.length > 0 && (
            <button
              type="button"
              onClick={() => setNoticesOpen(o => !o)}
              className="px-2 py-0.5 rounded text-xs font-mono bg-red-900/60 text-red-300 border border-red-700 flex items-center gap-1"
              title="Playback is blocked — click to see why"
            >
              <AlertTriangle size={12} /> {stale.length} notice{stale.length > 1 ? 's' : ''} <ChevronDown size={12} />
            </button>
          )}
          {!fatal && stale.length > 0 && (
            <button
              type="button"
              onClick={() => setNoticesOpen(o => !o)}
              className="px-2 py-0.5 rounded text-xs font-mono bg-amber-900/60 text-amber-300 border border-amber-700 flex items-center gap-1"
              title="Data or geometry issues the preview worked around — click to expand"
            >
              <AlertTriangle size={12} /> {stale.length} notice{stale.length > 1 ? 's' : ''} <ChevronDown size={12} />
            </button>
          )}
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded pointer-coarse:min-h-11 pointer-coarse:min-w-11" aria-label="Close preview">
          <X size={20} className="text-slate-300" />
        </button>
      </div>

      {/* Stop banner — always visible, never behind a toggle: playback
          halts at the first pass that cannot be animated (backend BLOCKED
          or an invariant inconsistency) and every later pass is withheld
          because it was planned assuming the earlier ones ran. */}
      {stopAt && (
        <div className="px-6 py-2 border-b shrink-0 text-xs bg-red-950/80 border-red-800 text-red-200">
          <div className="font-semibold mb-0.5">
            {inconsistency
              ? `Preview inconsistent — animation stops at ${inconsistency.label}: ${markOf(inconsistency.reason)}`
              : `Animation stops at ${stopAt.label} — ${markOf(stopAt.reason)}`}
          </div>
          {blockedPasses.length > 0 && (
            <ul className="space-y-0.5">
              {blockedPasses.map((b, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <OctagonX size={12} className="mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium">{b.label}</span> — {markOf(b.reason)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Expanded playback notices — advisory (amber) or fatal (red). */}
      {noticesOpen && stale.length > 0 && (
        <div
          className={`px-6 py-2 border-b shrink-0 text-xs ${
            fatal
              ? 'bg-red-950/70 border-red-800 text-red-300'
              : 'bg-amber-950/50 border-amber-800 text-amber-200'
          }`}
        >
          <div className="font-semibold mb-1">
            {fatal
              ? 'Playback blocked — these issues must be fixed first:'
              : `${noticeGroups.length} notice${noticeGroups.length > 1 ? 's' : ''} — the preview worked around these:`}
          </div>
          <ul className="space-y-0.5">
            {noticeGroups.map((g, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="opacity-60">•</span>
                <span>
                  {markOf(describeNotice(g.body))}
                  {g.refs.length > 0 && (
                    <span className="opacity-60"> — {g.refs.join(', ')}</span>
                  )}
                  {g.refs.length === 0 && g.count > 1 && (
                    <span className="opacity-60"> — ×{g.count}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Plan advisories — always amber, always visible: WARN findings
          from the backend (unseated cars shown approximately, etc). */}
      {warningGroups.length > 0 && (
        <div className="px-6 py-2 border-b shrink-0 text-xs bg-amber-950/50 border-amber-800 text-amber-200">
          <div className="font-semibold mb-1">
            {warningGroups.length === 1
              ? '1 plan advisory'
              : `${warningGroups.length} plan advisories`}
            {' — positions below are approximate:'}
          </div>
          <ul className="space-y-0.5">
            {warningGroups.map((g, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="opacity-60">•</span>
                <span>
                  {markOf(g.body)}
                  {g.refs.length > 0 && (
                    <span className="opacity-60"> — {g.refs.join(', ')}</span>
                  )}
                  {g.refs.length === 0 && g.count > 1 && (
                    <span className="opacity-60"> — ×{g.count}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="relative flex-1 min-h-0">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            Loading yard geometry…
          </div>
        )}
        {!loading && fatal && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-md text-center">
              <AlertTriangle size={32} className="mx-auto mb-3 text-red-400" />
              <p className="text-slate-200 font-medium mb-2">Cannot animate — geometry missing</p>
              <ul className="text-sm text-slate-400 space-y-1">
                {stale.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        <YardMapCanvas
          geoData={previewGeoData}
          yardAssets={[]}
          activeTheme="midnight"
          cameraState={cameraState}
          canvasOffset={canvasOffset}
          stageSize={stageSize}
          yardOrientation={yardOrientation}
          previewSnapshot={snapshot}
          previewCarLengths={previewCarLengths}
          previewCarMeta={carMeta}
          previewSuppressApproxDots={allApprox}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          isDragging={dragRef.current != null || touchGesture.current.mode !== 'none'}
        />
        {/* Camera controls — wheel/drag on the canvas also work; any
            manual move pauses Follow until Re-center. */}
        {cameraState && (
          <div className="absolute right-4 top-4 flex flex-col gap-1.5">
            <button
              onClick={() => zoomButton(1.4)}
              className="p-1.5 rounded bg-slate-900/85 border border-slate-700 text-slate-200 hover:bg-slate-800 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
              title="Zoom in"
              aria-label="Zoom in"
            >
              <ZoomIn size={16} />
            </button>
            <button
              onClick={() => zoomButton(1 / 1.4)}
              className="p-1.5 rounded bg-slate-900/85 border border-slate-700 text-slate-200 hover:bg-slate-800 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
              title="Zoom out"
              aria-label="Zoom out"
            >
              <ZoomOut size={16} />
            </button>
            <button
              onClick={fitToYard}
              className="p-1.5 rounded bg-slate-900/85 border border-slate-700 text-slate-200 hover:bg-slate-800 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
              title="Fit to yard"
              aria-label="Fit to yard"
            >
              <Maximize2 size={16} />
            </button>
            <button
              onClick={reCenter}
              className={`p-1.5 rounded border text-slate-200 transition-colors pointer-coarse:min-h-11 pointer-coarse:min-w-11 ${
                followPaused
                  ? 'bg-blue-600/90 border-blue-500 hover:bg-blue-500'
                  : 'bg-slate-900/85 border-slate-700 hover:bg-slate-800'
              }`}
              title={followPaused ? 'Re-center on consist (resume follow)' : 'Following consist'}
              aria-label="Re-center on consist"
            >
              <LocateFixed size={16} />
            </button>
          </div>
        )}
        {/* Marker legend — explains approx dots + hazmat diamonds. */}
        {(anyApprox || hasHazmat) && (
          <div className="absolute left-4 bottom-4 flex items-center gap-4 px-3 py-1.5 rounded bg-slate-900/85 border border-slate-700 text-[11px] text-slate-300 pointer-events-none">
            {allApprox ? (
              <span className="text-amber-300">
                Inbound cars shown at the couple junction; positions approximate
              </span>
            ) : (
              anyApprox && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
                  Approximate position
                </span>
              )
            )}
            {hasHazmat && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rotate-45 bg-amber-500 border border-amber-800 inline-block" />
                Hazmat
              </span>
            )}
          </div>
        )}
      </div>

      {/* Transport bar */}
      <div className="shrink-0 border-t border-slate-700 bg-slate-900 px-6 py-3">
        <div className="flex items-center gap-4">
          <button
            onClick={playback.restart}
            className="p-2 hover:bg-white/10 rounded text-slate-200 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
            title="Restart"
            disabled={!timeline}
          >
            <RotateCcw size={18} />
          </button>
          <button
            onClick={() => playback.stepBoundary(-1)}
            className="p-2 hover:bg-white/10 rounded text-slate-200 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
            title="Previous step"
            disabled={!timeline}
          >
            <SkipBack size={18} />
          </button>
          <button
            onClick={playback.toggle}
            className="p-2.5 hover:bg-white/10 rounded bg-blue-600 text-white pointer-coarse:min-h-11 pointer-coarse:min-w-11"
            title={playback.playing ? 'Pause' : 'Play'}
            disabled={!timeline}
          >
            {playback.playing ? <Pause size={20} /> : <Play size={20} />}
          </button>
          <button
            onClick={() => playback.stepBoundary(1)}
            className="p-2 hover:bg-white/10 rounded text-slate-200 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
            title="Next step"
            disabled={!timeline}
          >
            <SkipForward size={18} />
          </button>
          <input
            type="range"
            min={0}
            max={playback.totalFeet || 1}
            value={playback.feet}
            onChange={e => playback.seek(Number(e.target.value))}
            className="w-44 shrink-0 accent-blue-500"
            disabled={!timeline}
          />
          <span
            className="text-xs text-slate-400 font-mono tabular-nums shrink-0 w-24 text-center"
            title="Elapsed / total (virtual time at 1×)"
          >
            {fmtTime(playback.feet)} / {fmtTime(playback.totalFeet)}
          </span>
          <select
            value={playback.speedMult}
            onChange={e => playback.setSpeedMult(Number(e.target.value))}
            className="bg-slate-800 text-slate-200 text-sm rounded px-2 py-1 border border-slate-600 pointer-coarse:min-h-11"
          >
            {PREVIEW_SPEEDS.map(s => (
              <option key={s} value={s}>{s}×</option>
            ))}
          </select>
          <div className="flex-1 min-w-0 text-sm text-slate-300 font-mono truncate text-right" title={stepLabel}>
            {stepLabel}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SwitchListPreviewMapModal
